import React, { useState } from "react";
import Button from "@mui/material/Button";
import "./StartingQuestionnaire.css";

const CONSENT_VERSION = "2026-08-07";

const CONSENT_SECTIONS = [
  {
    title: "Informed Consent Form Of Participation",
    paragraphs: [
      "You are invited to participate in a user study Exploring Interactive Explainable AI Methods conducted by Omair Shahzad Bhatti and Abdulrahman Mohamed. The research is supervised by Michael Barz and funded by the German Federal Ministry for Research, Technology and Aeronautics (BMFTR) through the project No-IDLE (01 IW23002).",
      "This is an online study conducted via Prolific. The alternative to participation in this study is to choose not to participate. If you have any questions or complaints about the whole informed consent process of this research study or your rights as a human research subject, please contact Omair Shahzad Bhatti (E-Mail: omair_shahzad.bhatti@dfki.de) and Abdulrahman Mohamed (E-Mail: abdulrahman.mohamed@dfki.de).",
      "You should carefully read the information below. Please take the time you need to read the consent form.",
    ],
    bullets: [
      "You must be at least 18 years old to take part; the study is open to both students and non-students.",
      "Participation is entirely voluntary and may be withdrawn at any time.",
      "The user study will last approximately 30 to 45 minutes.",
      "We will collect personal demographics (age group, profession, etc.).",
      "All records and data will be treated according to standard data use policies.",
      "All records and subject-related data will be anonymised.",
      "Repeated participation in the study is not permitted.",
    ],
  },
  {
    title: "Participation And Compensation",
    paragraphs: [
      "Your participation in this user study is completely voluntary and takes place online via Prolific. Compensation is handled through Prolific at the rate shown on the Prolific study listing. You must complete the full study to receive the Prolific payout; incomplete sessions may not be eligible for payment.",
      "If you decline to participate or withdraw from the user study, there will be no negative consequences for you. The investigator may withdraw you from this research if continued participation would no longer serve the study's goals or could affect your well-being.",
    ],
  },
  {
    title: "Risks And Benefits",
    paragraphs: [
      "This study involves viewing travel photos and rating AI-generated captions on a screen. There are no physical risks, and any inconvenience is minor, mainly the 30 to 45 minutes it takes.",
    ],
  },
  {
    title: "Data Protection And Confidentiality",
    paragraphs: [
      "We plan to publish the results from this and other sessions in scientific articles or related media, without including your name or any information that could be associated with your identity. Any demographic information will be anonymised and presented only in aggregated form. All data and information obtained in this user study will be treated confidentially, stored in encrypted form, and inaccessible to anyone outside this research project unless you provide explicit consent through a separate permission form.",
      "The data you provide will be subject to the General Data Protection Regulation (GDPR) of the European Union and processed in full compliance with its provisions. Any subsequent use of records and data will follow standard data use policies that ensure the anonymity of participants.",
      "During the study, we will log experimental data. All raw data and related material will be securely retained, in compliance with GDPR, for no longer than the period required by the funding organisation (10 years), unless you request their immediate deletion.",
      "Although every precaution will be taken to safeguard confidentiality and anonymity, there is always a minimal risk of breach inherent in any publication or online activity. In accordance with the GDPR, researchers will notify participants if a breach of confidential data is detected.",
    ],
  },
  {
    title: "Contact",
    paragraphs: [
      "If you have any questions or concerns about the research, please feel free to contact:",
    ],
    bullets: [
      "Omair Shahzad Bhatti (Experimenter), omair_shahzad.bhatti@dfki.de",
      "Abdulrahman Mohamed (Experimenter), abdulrahman.mohamed@dfki.de",
      "Michael Barz (Supervisor), michael.barz@dfki.de",
    ],
    closing: "Deutsches Forschungszentrum für Künstliche Intelligenz GmbH, Saarland Informatics Campus D3 2, 66123 Saarbrücken, Germany",
  },
];

function ConsentForm({ onConsent, onDecline }) {
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState("");

  const handleContinue = () => {
    if (!accepted) {
      setError("Please tick the consent box if you would like to take part.");
      return;
    }

    onConsent({
      consentAccepted: true,
      consentVersion: CONSENT_VERSION,
      consentTimestamp: new Date().toISOString(),
    });
  };

  return (
    <div className="root-container">
      <div className="app">
        <div
          className={`form-container consent-container${accepted ? "" : " consent-container--pending"}`}
          data-testid="consent-container"
        >
          <h1>Before You Decide To Take Part</h1>
          <div className="section consent-copy">
            <div className="consent-scroll-panel" tabIndex="0" aria-label="Study information and data consent text">
              {CONSENT_SECTIONS.map((section) => (
                <section className="consent-text-section" key={section.title}>
                  <h2>{section.title}</h2>
                  {section.paragraphs?.map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                  ))}
                  {section.bullets && (
                    <ul>
                      {section.bullets.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  )}
                  {section.ordered && (
                    <ol>
                      {section.ordered.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ol>
                  )}
                  {section.closing && <p>{section.closing}</p>}
                </section>
              ))}
            </div>
            <label className="consent-checkbox">
              <input
                type="checkbox"
                checked={accepted}
                onChange={(event) => {
                  setAccepted(event.target.checked);
                  setError("");
                }}
              />
              <span>
                I have read the study information and consent to participate in
                this study.
              </span>
            </label>
            {error && <div className="validation-error">{error}</div>}
          </div>
          <div className="navigation-buttons">
            <Button variant="outlined" color="inherit" onClick={onDecline}>
              I do not agree
            </Button>
            <Button
              className="consent-continue-button"
              variant="contained"
              onClick={handleContinue}
              disabled={!accepted}
            >
              I agree and want to continue
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ConsentForm;